import { useCallback, useEffect, useState } from 'react';
import { useSession } from '@clerk/clerk-react';
import * as Sentry from '@sentry/react';
import { toast } from 'react-toastify';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';

/** Melding van een mislukte call, met de backend-boodschap wanneer die er is. */
const melding = (error: unknown, fallback: string): string =>
  error instanceof Error && error.message ? error.message : fallback;

/**
 * Eén categorie uit de gedeelde lijst, met haar gebruik.
 *
 * Dit is dezelfde lijst als de categorieën van een projectonderdeel
 * (`renovation_label`) — vandaar `partCount`: een categorie die op projectonderdelen
 * staat, mag niet vanuit een ticket weggegooid worden.
 */
export interface RenoTag {
  id: number;
  name: string;
  /** Soort categorie: algemeen, esthetisch, energetisch, veiligheid. */
  type?: string;
  ticketCount: number;
  /** Aantal projectonderdelen dat deze categorie gebruikt. */
  partCount?: number;
}

/**
 * De categorieën die je op een renovatieticket kan kiezen: **dezelfde lijst** als de
 * categorieën van een projectonderdeel (`renovation_label`).
 *
 * Eerder had het ticket zijn eigen lijst, waardoor "afbraak" op een ticket en
 * "Afbraakwerken" op een onderdeel naast elkaar bestonden en je ze nooit samen kon
 * filteren. Wat je hier toevoegt is dus ook in de renovatiemodule kiesbaar; wat je
 * verwijdert verdwijnt van de tickets waar het op stond — maar een categorie die op
 * projectonderdelen staat (`partCount > 0`) kan niet vanuit een ticket verwijderd worden.
 */
export function useRenoTags() {
  const { session } = useSession();
  const [tags, setTags] = useState<RenoTag[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!session) return;
    setIsLoading(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/reno-helpdesk/tags`, { method: 'GET' }, session);
      if (!res.ok) throw new Error('Categorieën ophalen mislukt');
      setTags(await res.json());
    } catch (error) {
      // Zonder catalogus kan je geen tag kiezen — maak dat zichtbaar i.p.v. een lege lijst.
      Sentry.captureException(error, { tags: { module: 'reno-helpdesk', action: 'listTags' } });
      toast.error('De categorielijst kon niet geladen worden');
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  useEffect(() => { refresh(); }, [refresh]);

  /** Nieuwe tag aanmaken; ze komt meteen in de lijst van selecteerbare tags. */
  const createTag = useCallback(
    async (name: string): Promise<RenoTag | null> => {
      if (!session) return null;
      try {
        const res = await fetchWithAuth(
          `${API_URL}/reno-helpdesk/tags`,
          { method: 'POST', body: JSON.stringify({ name }), headers: { 'Content-Type': 'application/json' } },
          session,
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.message || 'Categorie toevoegen mislukt');

        // Lokaal bijzetten (alfabetisch, zoals de backend sorteert) i.p.v. alles opnieuw
        // ophalen: de kiezer staat open en mag niet in laadstatus springen.
        setTags((prev) =>
          prev.some((t) => t.id === data.id)
            ? prev
            : [...prev, data as RenoTag].sort((a, b) => a.name.localeCompare(b.name)),
        );
        return data as RenoTag;
      } catch (error) {
        Sentry.captureException(error, { tags: { module: 'reno-helpdesk', action: 'createTag' } });
        toast.error(melding(error, 'Categorie toevoegen mislukt'));
        return null;
      }
    },
    [session],
  );

  /**
   * Tag verwijderen uit de catalogus én van alle tickets waar ze op staat.
   * Geeft terug van hoeveel tickets ze gehaald is, zodat de UI dat kan melden.
   */
  const deleteTag = useCallback(
    async (tag: RenoTag): Promise<{ removedFromTickets: number } | null> => {
      if (!session) return null;
      try {
        const res = await fetchWithAuth(
          `${API_URL}/reno-helpdesk/tags/${tag.id}`,
          { method: 'DELETE' },
          session,
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.message || 'Categorie verwijderen mislukt');

        setTags((prev) => prev.filter((t) => t.id !== tag.id));
        return data as { removedFromTickets: number };
      } catch (error) {
        Sentry.captureException(error, { tags: { module: 'reno-helpdesk', action: 'deleteTag' } });
        toast.error(melding(error, 'Categorie verwijderen mislukt'));
        return null;
      }
    },
    [session],
  );

  return { tags, isLoading, refresh, createTag, deleteTag };
}
